/**
 * ConfirmDialogProvider -- mount once at the app root.
 *
 * Subscribes to the module-level store in `confirmDialog.ts` and renders
 * the AlertDialog when a confirm request is active.
 */

import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { listeners, current, _clearCurrent, type ConfirmState } from "@/components/confirmDialog";

export function ConfirmDialogProvider() {
  const [state, setState] = useState<ConfirmState | null>(current);

  useEffect(() => {
    listeners.push(setState);
    return () => {
      const idx = listeners.indexOf(setState);
      if (idx > -1) listeners.splice(idx, 1);
    };
  }, []);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && state) {
        state.resolve(false);
        _clearCurrent();
      }
    },
    [state]
  );

  const handleConfirm = useCallback(() => {
    if (!state) return;
    state.resolve(true);
    _clearCurrent();
  }, [state]);

  const handleCancel = useCallback(() => {
    if (!state) return;
    state.resolve(false);
    _clearCurrent();
  }, [state]);

  return (
    <AlertDialog open={state != null} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="bg-dashboard-surface-dark border-dashboard-border text-dashboard-text-primary">
        <AlertDialogHeader>
          <AlertDialogTitle>{state?.title}</AlertDialogTitle>
          <AlertDialogDescription className="text-slate-400">
            {state?.description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {state?.body}
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={handleCancel}
            className="bg-dashboard-surface-dark text-dashboard-text-primary border-dashboard-border hover:bg-dashboard-surface-light"
          >
            {state?.cancelLabel ?? "Cancel"}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            className={
              state?.destructive
                ? "bg-red-600 text-white hover:bg-red-700"
                : "bg-dashboard-text-primary text-dashboard-surface-dark hover:bg-dashboard-text-secondary"
            }
            disabled={state?.pending}
          >
            {state?.pending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {state?.confirmLabel ?? "Confirm"}
              </>
            ) : (
              (state?.confirmLabel ?? "Confirm")
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
